import React, { useState, useEffect } from "react";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";

const TITLE = "Kalkulator Donat - Hitung Kalori, Lemak, dan Gula Donat dengan Akurat";
const DESCRIPTION =
  "Kalkulator donat gratis - hitung kalori, lemak, gula, dan karbohidrat dalam donat berdasarkan jenis dan ukurannya. Alat yang sempurna untuk memantau pola makan Anda.";
const KEYWORDS =
  "kalkulator donat, kalori donat, donat kalori, kalori donat, lemak donat, gula donat, kalkulator kue, donut calories calculator, donut nutrition";
const OG_URL = "https://do-calculate.com/calculator/id/donut/";

const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Kalkulator Donat",
    description: DESCRIPTION,
    url: OG_URL,
    applicationCategory: "UtilityApplication",
    operatingSystem: "Any",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Beranda",
        item: "https://do-calculate.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Kalkulator",
        item: "https://do-calculate.com/id/all-calculators/",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "Kalkulator Donat",
        item: OG_URL,
      },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Berapa kalori dalam donat glazed?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Donat glazed mengandung sekitar 190-240 kalori untuk ukuran sedang, dengan sekitar 10-12 gram lemak dan 22-30 gram karbohidrat. Angkanya berbeda tergantung ukuran dan mereknya.",
        },
      },
      {
        "@type": "Question",
        name: "Apakah donat krim mengandung lebih banyak kalori dari donat biasa?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Ya, donat isi krim atau jeli biasanya mengandung 300-400 kalori, jauh lebih tinggi dari donat polos. Isiannya menambah lemak dan gula ekstra.",
        },
      },
      {
        "@type": "Question",
        name: "Jenis donat apa yang paling rendah kalori?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Donat polos atau yang ditaburi gula adalah yang paling rendah kalori, berkisar antara 170-220 kalori. Sedangkan donat hole paling ringan karena ukurannya kecil, hanya mengandung 60-100 kalori.",
        },
      },
    ],
  },
];

// Donut types with nutritional data per 100g
const DONUT_TYPES = {
  glazed: {
    name: "Donat Glazed",
    calories: 380,
    fat: 17,
    saturatedFat: 7,
    carbs: 52,
    sugar: 22,
    protein: 5,
    sodium: 280,
    fiber: 1,
    weightG: 60,
  },
  chocolate_glazed: {
    name: "Donat Glazed Cokelat",
    calories: 400,
    fat: 20,
    saturatedFat: 9,
    carbs: 52,
    sugar: 28,
    protein: 5,
    sodium: 290,
    fiber: 1.5,
    weightG: 75,
  },
  plain: {
    name: "Donat Polos",
    calories: 350,
    fat: 15,
    saturatedFat: 6,
    carbs: 48,
    sugar: 12,
    protein: 5,
    sodium: 260,
    fiber: 1,
    weightG: 55,
  },
  sugar: {
    name: "Donat Gula",
    calories: 390,
    fat: 17,
    saturatedFat: 7,
    carbs: 55,
    sugar: 20,
    protein: 5,
    sodium: 280,
    fiber: 1,
    weightG: 60,
  },
  powdered_sugar: {
    name: "Donat Gula Halus",
    calories: 395,
    fat: 18,
    saturatedFat: 7.5,
    carbs: 54,
    sugar: 21,
    protein: 5,
    sodium: 280,
    fiber: 1,
    weightG: 65,
  },
  cream_filled: {
    name: "Donat Isi Krim",
    calories: 420,
    fat: 22,
    saturatedFat: 10,
    carbs: 52,
    sugar: 25,
    protein: 5,
    sodium: 300,
    fiber: 0.5,
    weightG: 85,
  },
  jelly: {
    name: "Donat Isi Jeli",
    calories: 410,
    fat: 18,
    saturatedFat: 7,
    carbs: 58,
    sugar: 30,
    protein: 5,
    sodium: 290,
    fiber: 0.5,
    weightG: 85,
  },
  frosted: {
    name: "Donat Frosting",
    calories: 430,
    fat: 21,
    saturatedFat: 9,
    carbs: 58,
    sugar: 32,
    protein: 5,
    sodium: 300,
    fiber: 0.5,
    weightG: 75,
  },
  old_fashioned: {
    name: "Donat Old Fashioned",
    calories: 450,
    fat: 22,
    saturatedFat: 10,
    carbs: 58,
    sugar: 22,
    protein: 6,
    sodium: 310,
    fiber: 1,
    weightG: 80,
  },
  donut_hole: {
    name: "Donat Hole (donut hole)",
    calories: 370,
    fat: 16,
    saturatedFat: 6,
    carbs: 51,
    sugar: 18,
    protein: 5,
    sodium: 270,
    fiber: 1,
    weightG: 25,
  },
};

const SIZE_MULTIPLIERS = {
  mini: { name: "Mini (sangat kecil)", multiplier: 0.5 },
  small: { name: "Kecil", multiplier: 0.75 },
  medium: { name: "Sedang", multiplier: 1.0 },
  large: { name: "Besar", multiplier: 1.35 },
  extra_large: { name: "Sangat Besar", multiplier: 1.7 },
};

function DonutCalculator() {
  const [donutType, setDonutType] = useState("glazed");
  const [size, setSize] = useState("medium");
  const [quantity, setQuantity] = useState(1);
  const [customWeight, setCustomWeight] = useState("");
  const [useCustomWeight, setUseCustomWeight] = useState(false);
  const [dailyCalories, setDailyCalories] = useState(2000);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const calculateResult = () => {
    try {
      setError("");
      const donut = DONUT_TYPES[donutType];
      const sizeMultiplier = SIZE_MULTIPLIERS[size].multiplier;

      let weightGrams;
      if (useCustomWeight && customWeight) {
        weightGrams = parseFloat(customWeight);
        if (isNaN(weightGrams) || weightGrams <= 0) {
          setError("Harap masukkan berat yang valid lebih dari nol");
          setResult(null);
          return;
        }
      } else {
        weightGrams = donut.weightG * sizeMultiplier;
      }

      const qty = parseInt(quantity) || 1;
      const factor = weightGrams / 100;

      const caloriesPerPiece = donut.calories * factor;
      const fatPerPiece = donut.fat * factor;
      const saturatedFatPerPiece = donut.saturatedFat * factor;
      const carbsPerPiece = donut.carbs * factor;
      const sugarPerPiece = donut.sugar * factor;
      const proteinPerPiece = donut.protein * factor;
      const sodiumPerPiece = donut.sodium * factor;
      const fiberPerPiece = donut.fiber * factor;

      const totalCalories = caloriesPerPiece * qty;
      const totalFat = fatPerPiece * qty;
      const totalSaturatedFat = saturatedFatPerPiece * qty;
      const totalCarbs = carbsPerPiece * qty;
      const totalSugar = sugarPerPiece * qty;
      const totalProtein = proteinPerPiece * qty;
      const totalSodium = sodiumPerPiece * qty;
      const totalFiber = fiberPerPiece * qty;

      const dailyCaloriesPercent = (totalCalories / dailyCalories) * 100;
      const dailyFatPercent = (totalFat / 78) * 100; // based on 2000 cal diet 78g fat
      const dailySugarPercent = (totalSugar / 50) * 100; // 50g daily sugar limit

      // Macro distribution
      const fatCalories = totalFat * 9;
      const carbCalories = totalCarbs * 4;
      const proteinCalories = totalProtein * 4;
      const totalMacroCalories = fatCalories + carbCalories + proteinCalories;

      const fatPercent = (fatCalories / totalMacroCalories) * 100;
      const carbPercent = (carbCalories / totalMacroCalories) * 100;
      const proteinPercent = (proteinCalories / totalMacroCalories) * 100;

      setResult({
        weightGrams,
        qty,
        donutName: donut.name,
        sizeName: SIZE_MULTIPLIERS[size].name,
        caloriesPerPiece,
        totalCalories,
        totalFat,
        totalSaturatedFat,
        totalCarbs,
        totalSugar,
        totalProtein,
        totalSodium,
        totalFiber,
        dailyCaloriesPercent,
        dailyFatPercent,
        dailySugarPercent,
        fatPercent,
        carbPercent,
        proteinPercent,
      });
    } catch (err) {
      setError("Kesalahan dalam perhitungan: " + err.message);
      setResult(null);
    }
  };

  useEffect(() => {
    calculateResult();
  }, [donutType, size, quantity, customWeight, useCustomWeight, dailyCalories]);

  const handleClear = () => {
    setDonutType("glazed");
    setSize("medium");
    setQuantity(1);
    setCustomWeight("");
    setUseCustomWeight(false);
    setDailyCalories(2000);
    setResult(null);
    setError("");
  };

  return (
    <div className="ltr" style={{ direction: "ltr" }}>
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
        SCHEMA={SCHEMA}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ padding: "15px", borderRadius: "5px" }}
          >
            <h1 style={{ fontSize: "22px", margin: 0 }}>Kalkulator Donat</h1>
          </div>
          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            {/* Introduction */}
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                <strong>Kalkulator Donat</strong> adalah alat gratis dan akurat yang membantu Anda{" "}
                <strong>menghitung kalori</strong>, lemak, gula,
                dan karbohidrat dalam donat berdasarkan jenis, ukuran, dan jumlahnya. Baik Anda
                sedang memantau pola makan atau sekadar ingin tahu apa yang Anda makan, alat ini
                memberikan jawaban instan dan akurat.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />
            {/* Calculator Section */}
            <div className="calculatorStyle mb-4">
              <div className="p-3 mb-3 bg-success text-white text-center rounded">
                <h2 style={{ fontSize: "18px", margin: 0 }}>
                  Hitung Kalori Donat Anda
                </h2>
              </div>

              {/* Donut Type */}
              <div className="row">
                <div className="col-sm-12">
                  <label htmlFor="donutType" >
                    Jenis Donat
                  </label>
                  <select
                    id="donutType"
                    className="form-select"
                    value={donutType}
                    onChange={(e) => setDonutType(e.target.value)}
                  >
                    {Object.entries(DONUT_TYPES).map(([key, val]) => (
                      <option key={key} value={key}>
                        {val.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Size */}
              <div className="row">
                <div className="col-sm-12 col-md-6">
                  <label htmlFor="size" >
                    Ukuran
                  </label>
                  <select
                    id="size"
                    className="form-select"
                    value={size}
                    onChange={(e) => setSize(e.target.value)}
                    disabled={useCustomWeight}
                  >
                    {Object.entries(SIZE_MULTIPLIERS).map(([key, val]) => (
                      <option key={key} value={key}>
                        {val.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-sm-12 col-md-6">
                  <label htmlFor="quantity" >
                    Jumlah (potong)
                  </label>
                  <input
                    id="quantity"
                    type="number"
                    className="form-control"
                    value={quantity}
                    onChange={(e) =>
                      setQuantity(parseInt(e.target.value) || 1)
                    }
                    min="1"
                    max="50"
                    placeholder="Jumlah"
                  />
                </div>
              </div>

              {/* Custom Weight Toggle */}
              <div className="row">
                <div className="col-sm-12">
                  <div style={{ marginTop: "8px", marginBottom: "4px" }}>
                    <input
                      type="checkbox"
                      id="useCustomWeight"
                      checked={useCustomWeight}
                      onChange={(e) => setUseCustomWeight(e.target.checked)}
                      style={{ marginRight: "8px" }}
                    />
                    <label
                      htmlFor="useCustomWeight"
                      style={{ cursor: "pointer" }}
                    >
                      Masukkan berat secara manual (gram)
                    </label>
                  </div>
                </div>
              </div>

              {useCustomWeight && (
                <div className="row">
                  <div className="col-sm-12 col-md-6">
                    <label htmlFor="customWeight" >
                      Berat per potong (gram)
                    </label>
                    <input
                      id="customWeight"
                      type="number"
                      className="form-control"
                      value={customWeight}
                      onChange={(e) => setCustomWeight(e.target.value)}
                      min="1"
                      step="1"
                      placeholder="Contoh: 75"
                      style={{ backgroundColor: "#fff3cd" }}
                    />
                  </div>
                </div>
              )}

              {/* Daily Calories */}
              <div className="row">
                <div className="col-sm-12 col-md-6">
                  <label htmlFor="dailyCalories" >
                    Kebutuhan kalori harian Anda (kkal)
                  </label>
                  <input
                    id="dailyCalories"
                    type="number"
                    className="form-control"
                    value={dailyCalories}
                    onChange={(e) =>
                      setDailyCalories(parseInt(e.target.value) || 2000)
                    }
                    min="1000"
                    max="5000"
                    step="50"
                    placeholder="2000"
                  />
                </div>
              </div>

              {/* Buttons */}
              <div className="text-center mb-3 mt-4">
                <button
                  onClick={handleClear}
                  className="btn btn-secondary"
                  style={{
                    fontSize: "16px",
                    padding: "10px 30px",
                  }}
                >
                  Hapus
                </button>
              </div>

              {/* Error Message */}
              {error && (
                <div className="alert alert-warning" role="alert">
                  <strong>Peringatan:</strong> {error}
                </div>
              )}

              {/* Results */}
              {result && (
                <div className="mt-4">
                  <h3
                    style={{
                      fontSize: "18px",
                      marginBottom: "15px",
                      textAlign: "center",
                    }}
                  >
                    <strong>Hasil:</strong>
                  </h3>
                  {/* Main Results Table */}
                  <div className="mb-3">
                    <table className="table bmi-table" style={{ width: "100%" }}>
                      <thead>
                        <tr>
                          <th
                            className="bmi-header"
                            style={{ textAlign: "center" }}
                          >
                            Keterangan
                          </th>
                          <th
                            className="bmi-header"
                            style={{ textAlign: "center" }}
                          >
                            Per Potong
                          </th>
                          <th
                            className="bmi-header"
                            style={{ textAlign: "center" }}
                          >
                            Total ({result.qty} potong)
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ textAlign: "center" }}>
                            Kalori
                          </td>
                          <td
                            style={{
                              textAlign: "center",
                              fontSize: "18px",
                              fontWeight: "bold",
                              color: "#1a5f3f",
                            }}
                          >
                            {result.caloriesPerPiece.toFixed(0)} kkal
                          </td>
                          <td
                            style={{
                              textAlign: "center",
                              fontSize: "18px",
                              fontWeight: "bold",
                              color: "#0f3d28",
                            }}
                          >
                            {result.totalCalories.toFixed(0)} kkal
                          </td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>Lemak</td>
                          <td style={{ textAlign: "center" }}>
                            {(result.totalFat / result.qty).toFixed(1)} g
                          </td>
                          <td style={{ textAlign: "center" }}>
                            {result.totalFat.toFixed(1)} g
                          </td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>Lemak Jenuh</td>
                          <td style={{ textAlign: "center" }}>
                            {(result.totalSaturatedFat / result.qty).toFixed(1)} g
                          </td>
                          <td style={{ textAlign: "center" }}>
                            {result.totalSaturatedFat.toFixed(1)} g
                          </td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>Karbohidrat</td>
                          <td style={{ textAlign: "center" }}>
                            {(result.totalCarbs / result.qty).toFixed(1)} g
                          </td>
                          <td style={{ textAlign: "center" }}>
                            {result.totalCarbs.toFixed(1)} g
                          </td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>Gula</td>
                          <td style={{ textAlign: "center" }}>
                            {(result.totalSugar / result.qty).toFixed(1)} g
                          </td>
                          <td style={{ textAlign: "center" }}>
                            {result.totalSugar.toFixed(1)} g
                          </td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>Protein</td>
                          <td style={{ textAlign: "center" }}>
                            {(result.totalProtein / result.qty).toFixed(1)} g
                          </td>
                          <td style={{ textAlign: "center" }}>
                            {result.totalProtein.toFixed(1)} g
                          </td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>Serat</td>
                          <td style={{ textAlign: "center" }}>
                            {(result.totalFiber / result.qty).toFixed(1)} g
                          </td>
                          <td style={{ textAlign: "center" }}>
                            {result.totalFiber.toFixed(1)} g
                          </td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>Natrium</td>
                          <td style={{ textAlign: "center" }}>
                            {(result.totalSodium / result.qty).toFixed(0)} mg
                          </td>
                          <td style={{ textAlign: "center" }}>
                            {result.totalSodium.toFixed(0)} mg
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="info-sections-container">
                    <div className="info-section">
                      <div className="info-section-header">
                        <strong>Persentase dari Kebutuhan Harian Anda</strong>
                      </div>
                      <div className="info-section-body">
                        <ul
                          style={{ listStyle: "none", padding: 0, lineHeight: "2" }}
                        >
                          <li>
                            🔥 <strong>Kalori:</strong>{" "}
                            {result.dailyCaloriesPercent.toFixed(1)}% dari{" "}
                            {dailyCalories} kkal per hari
                          </li>
                          <li>
                            🧈 <strong>Lemak:</strong>{" "}
                            {result.dailyFatPercent.toFixed(1)}% dari batas harian yang direkomendasikan (78 g)
                          </li>
                          <li>
                            🍬 <strong>Gula:</strong>{" "}
                            {result.dailySugarPercent.toFixed(1)}% dari batas harian yang direkomendasikan (50 g)
                          </li>
                        </ul>
                        <div
                          className="alert alert-info mt-3"
                          style={{ fontSize: "14px" }}
                        >
                          <strong>💡 Info:</strong> Jenis:{" "}
                          {result.donutName} — Berat:{" "}
                          {result.weightGrams.toFixed(0)} g per potong
                        </div>
                      </div>
                    </div>
                    <div className="info-section">
                      <div className="info-section-header">
                        <strong>Distribusi Makronutrien</strong>
                      </div>
                      <div className="info-section-body">
                        <div style={{ lineHeight: "1.8" }}>
                          <p>
                            <strong>Lemak:</strong>{" "}
                            {result.fatPercent.toFixed(1)}% dari total kalori
                          </p>
                          <p>
                            <strong>Karbohidrat:</strong>{" "}
                            {result.carbPercent.toFixed(1)}% dari total kalori
                          </p>
                          <p>
                            <strong>Protein:</strong>{" "}
                            {result.proteinPercent.toFixed(1)}% dari total kalori
                          </p>
                          <hr />
                          <p style={{ fontSize: "14px", color: "#666" }}>
                            <strong>Cara Perhitungan:</strong>
                          </p>
                          <p style={{ fontSize: "14px", color: "#666" }}>
                            Berat per potong = {result.weightGrams.toFixed(0)} g
                            <br />
                            Kalori per 100 g ={" "}
                            {DONUT_TYPES[donutType].calories} kkal
                            <br />
                            Kalori per potong ={" "}
                            {result.caloriesPerPiece.toFixed(0)} kkal
                            <br />
                            Total = {result.totalCalories.toFixed(0)} kkal (
                            {result.qty} potong)
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <hr />
            {/* Related Links */}
            <div className="row">
              <RelatedLinks />
            </div>
          </div>
        </div>
        <div className="col-sm-12 col-md-4">
          <SecondAds />
          <br />
          <MainCalculatorsList />
        </div>
      </div>
      {/* Informational Content */}
      <div className="row" style={{ marginTop: "20px" }}>
        <div className="col-12 col-md-8">
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header">
                <strong>Apa itu Kalkulator Donat?</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Kalkulator Donat</strong> adalah alat gizi gratis yang secara akurat
                  menghitung kalori, lemak, gula, dan karbohidrat dalam donat. Nilai gizi
                  sangat bervariasi tergantung jenis dan ukuran donat, sehingga alat ini
                  sangat diperlukan bagi mereka yang menghitung kalori atau mengikuti pola
                  makan tertentu.
                </p>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  Cara menggunakan kalkulator:
                </h3>
                <ol style={{ lineHeight: "1.8" }}>
                  <li>Pilih jenis donat dari daftar (glazed, cokelat, krim...)</li>
                  <li>Tentukan ukuran: mini, sedang, atau besar</li>
                  <li>Masukkan jumlah potongan yang akan Anda makan</li>
                  <li>Masukkan kebutuhan kalori harian Anda untuk mengetahui persentasenya</li>
                  <li>Anda juga dapat memasukkan berat donat secara manual jika mengetahuinya</li>
                  <li>Hasil akan muncul otomatis tanpa perlu menekan tombol apa pun</li>
                </ol>
                <div
                  className="alert alert-info mt-3"
                  style={{ lineHeight: "1.8" }}
                >
                  <strong>💡 Catatan:</strong> Nilai gizi yang digunakan didasarkan
                  pada rata-rata yang terdokumentasi dari merek-merek terkemuka dan basis
                  data gizi. Nilai aktual dapat berbeda tergantung resep dan tempat pembelian.
                </div>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <strong>Kegunaan Kalkulator Donat</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>
                  Dalam manajemen berat badan dan gizi:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>Lacak Kalori:</strong> Bagi mereka yang menghitung kalori harian
                    dalam program diet
                  </li>
                  <li>
                    <strong>Pantau Gula:</strong> Sangat berguna bagi penderita diabetes
                    dan sensitivitas glukosa
                  </li>
                  <li>
                    <strong>Keseimbangan Gizi:</strong> Membantu Anda mengetahui berapa banyak
                    yang perlu dikompensasi dari makanan lain
                  </li>
                  <li>
                    <strong>Kurangi Lemak:</strong> Mengungkap perbedaan antar jenis donat
                    agar Anda memilih yang lebih ringan
                  </li>
                </ul>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  Di toko roti dan restoran:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>Label Gizi:</strong> Menyiapkan data gizi untuk produk
                  </li>
                  <li>
                    <strong>Pemasaran Sehat:</strong> Menonjolkan pilihan rendah kalori
                    kepada pelanggan
                  </li>
                  <li>
                    <strong>Perbandingan:</strong> Membandingkan berbagai produk berdasarkan
                    nilai gizinya
                  </li>
                  <li>
                    <strong>Pengembangan:</strong> Membantu mengembangkan resep yang lebih
                    ringan dan sehat
                  </li>
                </ul>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  Dalam kehidupan sehari-hari:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>Bersama Keluarga:</strong> Mengetahui berapa banyak kalori yang
                    dikonsumsi anak-anak dalam acara makan bersama
                  </li>
                  <li>
                    <strong>Acara Khusus:</strong> Merencanakan jumlah donat untuk pesta
                  </li>
                  <li>
                    <strong>Rasa Ingin Tahu Gizi:</strong> Memahami apa yang kita makan
                    dengan jelas
                  </li>
                  <li>
                    <strong>Pilihan Cerdas:</strong> Membandingkan berbagai jenis sebelum
                    membeli
                  </li>
                </ul>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <strong>Kalori dalam Jenis Donat yang Populer</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  Kandungan gizi donat berbeda-beda secara signifikan antar jenisnya. Donat
                  isi krim atau jeli biasanya mengandung kalori jauh lebih tinggi dari donat
                  polos, karena isiannya menambah berat, lemak, dan gula ekstra. Berikut
                  perbandingan singkatnya:
                </p>
                <table className="table bmi-table" style={{ marginTop: "1rem" }}>
                  <thead>
                    <tr>
                      <th
                        className="bmi-header"
                        style={{ textAlign: "center" }}
                      >
                        Jenis
                      </th>
                      <th
                        className="bmi-header"
                        style={{ textAlign: "center" }}
                      >
                        Berat Perkiraan (g)
                      </th>
                      <th
                        className="bmi-header"
                        style={{ textAlign: "center" }}
                      >
                        Kalori (kkal)
                      </th>
                      <th
                        className="bmi-header"
                        style={{ textAlign: "center" }}
                      >
                        Lemak (g)
                      </th>
                      <th
                        className="bmi-header"
                        style={{ textAlign: "center" }}
                      >
                        Gula (g)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ textAlign: "center" }}>Donat Polos</td>
                      <td style={{ textAlign: "center" }}>55</td>
                      <td style={{ textAlign: "center" }}>193</td>
                      <td style={{ textAlign: "center" }}>8.3</td>
                      <td style={{ textAlign: "center" }}>6.6</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>Donat Glazed</td>
                      <td style={{ textAlign: "center" }}>60</td>
                      <td style={{ textAlign: "center" }}>228</td>
                      <td style={{ textAlign: "center" }}>10.2</td>
                      <td style={{ textAlign: "center" }}>13.2</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>Donat Gula</td>
                      <td style={{ textAlign: "center" }}>60</td>
                      <td style={{ textAlign: "center" }}>234</td>
                      <td style={{ textAlign: "center" }}>10.2</td>
                      <td style={{ textAlign: "center" }}>12</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>Donat Isi Krim</td>
                      <td style={{ textAlign: "center" }}>85</td>
                      <td style={{ textAlign: "center" }}>357</td>
                      <td style={{ textAlign: "center" }}>18.7</td>
                      <td style={{ textAlign: "center" }}>21.3</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>Donat Isi Jeli</td>
                      <td style={{ textAlign: "center" }}>85</td>
                      <td style={{ textAlign: "center" }}>349</td>
                      <td style={{ textAlign: "center" }}>15.3</td>
                      <td style={{ textAlign: "center" }}>25.5</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>Donat Old Fashioned</td>
                      <td style={{ textAlign: "center" }}>80</td>
                      <td style={{ textAlign: "center" }}>360</td>
                      <td style={{ textAlign: "center" }}>17.6</td>
                      <td style={{ textAlign: "center" }}>17.6</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>Donat Hole</td>
                      <td style={{ textAlign: "center" }}>25</td>
                      <td style={{ textAlign: "center" }}>93</td>
                      <td style={{ textAlign: "center" }}>4</td>
                      <td style={{ textAlign: "center" }}>4.5</td>
                    </tr>
                  </tbody>
                </table>
                <div
                  className="alert alert-success mt-3"
                  style={{ lineHeight: "1.8" }}
                >
                  <strong>✓ Tips:</strong> Donat hole adalah pilihan cerdas bagi yang ingin
                  menikmati donat dengan kalori lebih sedikit. Ukurannya yang kecil berarti
                  Anda tetap bisa menikmati rasanya tanpa berlebihan dalam kalori.
                </div>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <strong>Contoh Praktis Menghitung Kalori Donat</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>
                  Contoh 1: Donat glazed ukuran sedang
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Situasi:</strong> Anda memakan satu donat glazed di pagi hari,
                  dan ingin tahu berapa persentasenya dari kebutuhan harian 2000 kkal.
                </p>
                <div className="alert alert-info" style={{ fontSize: "14px" }}>
                  <strong>Perhitungan:</strong> Berat donat = 60 g
                  <br />
                  Kalori per 100 g = 380 kkal
                  <br />
                  Kalori per potong = 60 × 380 / 100 = 228 kkal
                  <br />
                  Persentase dari hari = 228 / 2000 × 100 = 11,4%
                </div>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  Contoh 2: 3 buah donat hole
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Situasi:</strong> Anda memakan 3 donat hole bersama kopi pagi. Berapa kalorinya?
                </p>
                <div className="alert alert-info" style={{ fontSize: "14px" }}>
                  <strong>Perhitungan:</strong> Berat per potong = 25 g
                  <br />
                  Kalori per potong = 25 × 370 / 100 ≈ 93 kkal
                  <br />
                  Total untuk 3 potong = 93 × 3 = 279 kkal
                </div>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  Contoh 3: Donat isi krim ukuran besar
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Situasi:</strong> Donat isi krim ukuran besar — berapa lemak dan gula di dalamnya?
                </p>
                <div className="alert alert-info" style={{ fontSize: "14px" }}>
                  <strong>Perhitungan:</strong> Berat donat besar = 85 × 1,35 ≈ 115 g
                  <br />
                  Lemak = 115 × 22 / 100 ≈ 25,3 g
                  <br />
                  Gula = 115 × 25 / 100 ≈ 28,8 g (57% dari batas harian!)
                </div>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  Contoh 4: Merencanakan donat untuk pesta anak
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Situasi:</strong> Anda ingin menyajikan 20 donat glazed ukuran sedang untuk pesta anak. Berapa total kalorinya?
                </p>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Solusi:</strong> Masukkan ke kalkulator: Jenis = glazed, Ukuran =
                  sedang, Jumlah = 20. Hasilnya: sekitar 4.560 kkal total, yaitu 228
                  kkal per anak jika masing-masing mengambil satu.
                </p>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <strong>Tips Menikmati Donat dengan Cerdas</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>
                  Tips umum:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>Pilih Ukuran Lebih Kecil:</strong> Donat kecil memberi Anda
                    kepuasan yang sama dengan sekitar setengah kalorinya
                  </li>
                  <li>
                    <strong>Waktu itu Penting:</strong> Memakannya di pagi hari setelah
                    olahraga lebih baik daripada di malam hari
                  </li>
                  <li>
                    <strong>Jangan Makan Sendiri:</strong> Menambahkan protein seperti
                    telur atau yogurt memperlambat penyerapan gula
                  </li>
                  <li>
                    <strong>Moderasi adalah Kunci:</strong> Satu atau dua kali seminggu
                    masih dapat diterima dalam pola makan seimbang
                  </li>
                </ul>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  Kesalahan umum yang harus dihindari:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>Mengira donat kecil "tidak dihitung" — setiap kalori tetap terhitung</li>
                  <li>Mengabaikan kalori minuman pendamping seperti kopi manis</li>
                  <li>Membandingkan donat dengan kue sehat — keduanya adalah makanan penutup</li>
                  <li>Memakan lebih dari satu potong karena "ukurannya kecil" — jumlahnya melipat gandakan kalori</li>
                </ul>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  Untuk penggunaan profesional (toko roti dan restoran):
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>Gunakan opsi berat manual untuk memasukkan berat aktual resep Anda</li>
                  <li>Bandingkan berbagai jenis isian untuk memilih yang lebih ringan</li>
                  <li>Hitung kalori porsi penuh sebelum disajikan dalam acara</li>
                  <li>Gunakan angka-angka tersebut untuk menyiapkan label gizi produk yang akurat</li>
                </ul>
                <div
                  className="alert alert-warning mt-3"
                  style={{ lineHeight: "1.8" }}
                >
                  <strong>⚠️ Peringatan:</strong> Nilai gizi dalam kalkulator ini adalah
                  rata-rata standar. Donat buatan rumah atau toko roti lokal mungkin berbeda
                  tergantung jumlah mentega dan gula yang digunakan.
                </div>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <strong>Fakta Menarik tentang Donat</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>
                  Fakta sejarah dan gizi:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>Asal-usul Donat:</strong> Kemunculannya dikaitkan dengan
                    imigran Belanda di Amerika pada abad ke-19
                  </li>
                  <li>
                    <strong>Lubang Donat:</strong> Kisah terkenal menyebutkan bahwa Kapten
                    Hanson Gregory menemukan lubang pada tahun 1847 untuk memastikan adonan
                    matang merata
                  </li>
                  <li>
                    <strong>Hari Donat:</strong> Jumat pertama bulan Juni dirayakan sebagai
                    "Hari Donat Nasional" di Amerika Serikat
                  </li>
                  <li>
                    <strong>Angka Besar:</strong> Amerika Serikat saja mengonsumsi lebih
                    dari 10 miliar donat setiap tahunnya
                  </li>
                </ul>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  Donat di Indonesia:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>Sangat Populer:</strong> Donat telah menjadi salah satu kue
                    favorit anak-anak dan remaja di seluruh Indonesia
                  </li>
                  <li>
                    <strong>Rasa Lokal:</strong> Toko roti Indonesia mulai menawarkan donat
                    dengan rasa lokal seperti pandan, keju, dan cokelat
                  </li>
                  <li>
                    <strong>Acara Khusus:</strong> Donat sering disajikan dalam berbagai
                    acara dan perayaan sebagai camilan yang mudah dihidangkan
                  </li>
                </ul>
              </div>
            </div>

            {/* FAQ Section */}
            <div className="info-section">
              <div className="info-section-header">
                <strong>Pertanyaan Umum seputar Kalkulator Donat</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "8px" }}>
                  Berapa kalori dalam donat glazed?
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  Donat glazed ukuran sedang mengandung sekitar 220-240 kalori. Angka ini
                  tergantung pada berat aktualnya, di mana donat ukuran sedang berkisar
                  antara 55 dan 65 gram. Misalnya, donat glazed Krispy Kreme mengandung
                  190 kalori sementara produk Dunkin bisa mencapai 240 kalori.
                </p>

                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "8px",
                  }}
                >
                  Apa perbedaan antara donat ragi dan donat cake?
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  Donat ragi (yeast donut) umumnya lebih ringan karena mengandung rongga
                  udara yang membuatnya kurang padat, dengan kalori lebih rendah dari donat
                  cake yang lebih padat dan berlemak. Old fashioned misalnya adalah jenis
                  donat cake dan mengandung 310-350 kalori per potong sedang, lebih tinggi
                  dari donat ragi glazed.
                </p>

                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "8px",
                  }}
                >
                  Bisakah makan donat saat diet?
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  Ya, bisa dimakan secukupnya dalam diet seimbang apa pun. Yang terpenting
                  adalah menghitung kalorinya dalam anggaran harian Anda. Pilih ukuran kecil
                  atau donat hole untuk mengurangi kalori, dan hindari jenis isi krim jika
                  Anda memantau lemak. Satu kali seminggu tidak akan merusak program diet Anda.
                </p>

                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "8px",
                  }}
                >
                  Berapa gram lemak dalam donat isi krim?
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  Donat isi krim ukuran sedang mengandung sekitar 18-22 gram lemak. Ini
                  mewakili sekitar 25-28% dari batas lemak harian yang direkomendasikan
                  (78 g) untuk pola makan 2000 kalori. Lemak jenuh merupakan proporsi
                  yang signifikan karena krim yang digunakan dalam isiannya.
                </p>

                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "8px",
                  }}
                >
                  Donat apa yang paling aman untuk penderita diabetes?
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  Donat polos memiliki gula paling sedikit di antara jenis yang umum, hanya
                  mengandung 6-8 gram gula dibandingkan 25-30 gram pada jenis yang ada
                  isiannya. Meski demikian, semua jenis donat mengandung karbohidrat yang
                  cepat diserap. Penderita diabetes disarankan berkonsultasi dengan dokter
                  sebelum memasukkan donat dalam pola makan mereka.
                </p>

                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "8px",
                  }}
                >
                  Bagaimana cara menghitung kalori donat buatan rumah?
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  Gunakan opsi "Masukkan berat secara manual" di kalkulator dan masukkan
                  berat donat Anda dalam gram. Kalkulator akan memberi Anda perkiraan yang
                  baik berdasarkan jenis yang paling mendekati resep Anda. Untuk perhitungan
                  yang lebih akurat, hitung kalori setiap bahan (tepung, gula, telur, mentega,
                  minyak) dan bagi dengan jumlah donat yang dihasilkan.
                </p>

                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "8px",
                  }}
                >
                  Apakah donat hole lebih rendah kalori dari donat biasa?
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  Tentu saja, karena beratnya jauh lebih kecil. Donat hole hanya berbobot
                  20-30 gram dibandingkan 55-85 gram untuk donat biasa. Namun perhatikan:
                  jika Anda memakan 5 atau 6 buah donat hole, Anda mungkin melampaui kalori
                  satu donat biasa. Kesadaran akan jumlah adalah kunci yang sesungguhnya.
                </p>

                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "8px",
                  }}
                >
                  Apakah nilai kalori berbeda antar merek?
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  Ya, terkadang berbeda secara signifikan. Misalnya, donat glazed dari
                  Krispy Kreme mengandung 190 kalori sementara jenis yang sama dari Dunkin
                  mencapai 240 kalori. Perbedaan ini disebabkan oleh berat aktual dan bahan
                  yang digunakan dalam setiap resep. Untuk mengetahui nilai pasti suatu
                  merek, lihat informasi gizi resmi dari merek tersebut.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DonutCalculator;